import React, { useEffect, useRef, useState } from 'react';
import JSZip from 'jszip';
import { DocumentNode } from '../../types/documents';
import { documentDownloadService } from '../../services/documentDownloadService';

interface OpenDocumentViewerProps {
  document: DocumentNode;
  onClose: () => void;
}

const OpenDocumentViewer: React.FC<OpenDocumentViewerProps> = ({ document, onClose }) => {
  const [documentUrl, setDocumentUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const viewerRef = useRef<HTMLDivElement>(null);
  const viewerInstanceRef = useRef<any>(null);

  useEffect(() => {
    loadDocument();
    
    return () => {
      // Cleanup do viewer
      if (viewerInstanceRef.current) {
        viewerInstanceRef.current.destroy();
      }
    };
  }, [document]);

  const getFileExtension = (node: DocumentNode): string => {
    const fileName = node.metadata?.fileName || node.title || '';
    return fileName.split('.').pop()?.toLowerCase() || '';
  };

  const getFileType = (node: DocumentNode): string => {
    const ext = getFileExtension(node);
    
    if (ext === 'odt') return 'odt';
    if (ext === 'odp') return 'odp';
    if (ext === 'ods') return 'ods';
    return 'unknown';
  };

  const loadDocument = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const fileType = getFileType(document);
      
      if (fileType === 'unknown') {
        setError('Formato de arquivo não suportado');
        return;
      }

      const blob = await documentDownloadService.downloadDocument(document.id);
      const url = URL.createObjectURL(blob);
      setDocumentUrl(url);
      
    } catch (err) {
      console.error('Erro ao carregar documento:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (documentUrl && viewerRef.current) {
      // Importar ViewerJS dinamicamente
      import('viewerjs').then((ViewerJS) => {
        if (viewerRef.current) {
          // Limpar viewer anterior se existir
          if (viewerInstanceRef.current) {
            viewerInstanceRef.current.destroy();
          }

          // Configurar opções do ViewerJS
          const options = {
            inline: true,
            toolbar: {
              zoomIn: true,
              zoomOut: true,
              oneToOne: true,
              reset: true,
              prev: true,
              play: true,
              next: true,
              rotateLeft: true,
              rotateRight: true,
              flipHorizontal: true,
              flipVertical: true,
            },
            navbar: false,
            title: false,
            keyboard: true,
            focus: true,
            loading: true,
            loop: false,
            backdrop: false,
            button: true,
            fullscreen: true,
            zIndex: 9999,
            minZoomRatio: 0.1,
            maxZoomRatio: 10,
            zoomRatio: 0.1,
            zoomable: true,
            rotatable: true,
            scalable: true,
            toggleOnDblclick: true,
            tooltip: true,
            movable: true,
            ready: () => {
              console.log('ViewerJS carregado com sucesso');
            },
            show: () => {
              console.log('Documento exibido');
            },
            hide: () => {
              console.log('Documento ocultado');
            },
            view: () => {
              console.log('Documento visualizado');
            },
            viewed: () => {
              console.log('Documento visualizado completamente');
            },
            zoom: () => {
              console.log('Zoom alterado');
            },
            rotate: () => {
              console.log('Rotação alterada');
            },
            scale: () => {
              console.log('Escala alterada');
            },
            play: () => {
              console.log('Reprodução iniciada');
            },
            stop: () => {
              console.log('Reprodução parada');
            }
          };

          // Criar elemento para o documento
          const documentElement = createDocumentElement(documentUrl, getFileType(document));
          viewerRef.current.innerHTML = '';
          viewerRef.current.appendChild(documentElement);

          // Inicializar ViewerJS
          viewerInstanceRef.current = new ViewerJS.default(viewerRef.current, options);
        }
      }).catch((error) => {
        console.error('Erro ao carregar ViewerJS:', error);
        setError('Erro ao carregar visualizador');
      });
    }
  }, [documentUrl, document]);

  const createDocumentElement = (url: string, fileType: string): HTMLElement => {
    const container = window.document.createElement('div');
    container.className = 'opendocument-viewer-container';
    
    switch (fileType) {
      case 'odt':
        // Para ODT, extrair conteúdo usando JSZip
        container.innerHTML = `
          <div class="odt-viewer">
            <div class="loading-content">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p class="text-gray-600">Carregando documento ODT...</p>
            </div>
          </div>
        `;
        // Carregar conteúdo ODT de forma assíncrona
        loadODTContent(url, container);
        break;
        
      case 'odp':
        // Para ODP, usar iframe com Google Docs Viewer
        const odpIframe = window.document.createElement('iframe');
        odpIframe.src = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
        odpIframe.className = 'w-full h-full border-0';
        odpIframe.title = 'Apresentação ODP';
        container.appendChild(odpIframe);
        break;
        
      case 'ods':
        // Para ODS, usar iframe com Google Docs Viewer
        const odsIframe = window.document.createElement('iframe');
        odsIframe.src = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
        odsIframe.className = 'w-full h-full border-0';
        odsIframe.title = 'Planilha ODS';
        container.appendChild(odsIframe);
        break;
        
      default:
        const fallback = window.document.createElement('div');
        fallback.className = 'flex items-center justify-center h-full bg-gray-100';
        fallback.innerHTML = `
          <div class="text-center">
            <div class="text-gray-400 text-4xl mb-4">📄</div>
            <p class="text-gray-600">Formato não suportado para visualização</p>
            <a href="${url}" download class="mt-4 inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
              Baixar Arquivo
            </a>
          </div>
        `;
        container.appendChild(fallback);
    }
    
    return container;
  };

  const loadODTContent = async (url: string, container: HTMLElement) => {
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const zip = new JSZip();
      const zipFile = await zip.loadAsync(arrayBuffer);
      
      // Ler o arquivo content.xml do ODT
      const contentXml = await zipFile.file('content.xml')?.async('text');
      if (contentXml) {
        // Converter XML para HTML usando uma abordagem mais robusta
        const htmlContent = convertODTToHTML(contentXml);
        
        container.innerHTML = `
          <div class="odt-viewer">
            <div class="odt-content">
              ${htmlContent}
            </div>
          </div>
        `;
      } else {
        throw new Error('Não foi possível extrair o conteúdo do arquivo ODT');
      }
    } catch (error) {
      console.error('Erro ao carregar ODT:', error);
      container.innerHTML = `
        <div class="flex items-center justify-center h-full bg-gray-100">
          <div class="text-center">
            <div class="text-red-500 text-4xl mb-4">⚠️</div>
            <p class="text-gray-600 mb-4">Erro ao carregar documento ODT</p>
            <a href="${url}" download class="mt-4 inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
              Baixar Arquivo
            </a>
          </div>
        </div>
      `;
    }
  };

  const convertODTToHTML = (xmlContent: string): string => {
    // Implementação básica de conversão ODT XML para HTML
    let html = xmlContent
      // Converter parágrafos
      .replace(/<text:p[^>]*>/g, '<p>')
      .replace(/<\/text:p>/g, '</p>')
      // Converter cabeçalhos
      .replace(/<text:h[^>]*level="1"[^>]*>/g, '<h1>')
      .replace(/<text:h[^>]*level="2"[^>]*>/g, '<h2>')
      .replace(/<text:h[^>]*level="3"[^>]*>/g, '<h3>')
      .replace(/<text:h[^>]*level="4"[^>]*>/g, '<h4>')
      .replace(/<text:h[^>]*level="5"[^>]*>/g, '<h5>')
      .replace(/<text:h[^>]*level="6"[^>]*>/g, '<h6>')
      .replace(/<\/text:h[^>]*>/g, '</h1>')
      .replace(/<\/text:h[^>]*>/g, '</h2>')
      .replace(/<\/text:h[^>]*>/g, '</h3>')
      .replace(/<\/text:h[^>]*>/g, '</h4>')
      .replace(/<\/text:h[^>]*>/g, '</h5>')
      .replace(/<\/text:h[^>]*>/g, '</h6>')
      // Converter quebras de linha
      .replace(/<text:line-break\/>/g, '<br>')
      // Converter espaços
      .replace(/<text:s\/>/g, ' ')
      // Converter texto em negrito
      .replace(/<text:span[^>]*text:style-name="[^"]*Bold[^"]*"[^>]*>/g, '<strong>')
      .replace(/<\/text:span>/g, '</strong>')
      // Converter texto em itálico
      .replace(/<text:span[^>]*text:style-name="[^"]*Italic[^"]*"[^>]*>/g, '<em>')
      .replace(/<\/text:span>/g, '</em>')
      // Converter listas
      .replace(/<text:list[^>]*>/g, '<ul>')
      .replace(/<\/text:list>/g, '</ul>')
      .replace(/<text:list-item[^>]*>/g, '<li>')
      .replace(/<\/text:list-item>/g, '</li>')
      // Remover namespaces e tags desnecessárias
      .replace(/<[^>]*>/g, (match) => {
        if (match.includes('text:') || match.includes('office:') || match.includes('style:')) {
          return '';
        }
        return match;
      })
      // Limpar texto
      .replace(/\s+/g, ' ')
      .trim();

    return `
      <style>
        .odt-viewer {
          font-family: 'Times New Roman', serif;
          line-height: 1.6;
          padding: 20px;
          max-width: 800px;
          margin: 0 auto;
          background: white;
          box-shadow: 0 0 10px rgba(0,0,0,0.1);
          border-radius: 8px;
        }
        .odt-content h1, .odt-content h2, .odt-content h3, 
        .odt-content h4, .odt-content h5, .odt-content h6 {
          color: #333;
          margin-top: 20px;
          margin-bottom: 10px;
        }
        .odt-content p {
          margin-bottom: 10px;
        }
        .odt-content ul, .odt-content ol {
          margin: 15px 0;
          padding-left: 30px;
        }
        .odt-content li {
          margin-bottom: 5px;
        }
        .odt-content strong {
          font-weight: bold;
        }
        .odt-content em {
          font-style: italic;
        }
      </style>
      <div class="odt-content">${html}</div>
    `;
  };

  const getFileTypeLabel = (fileType: string): string => {
    switch (fileType) {
      case 'odt': return 'Documento de Texto';
      case 'odp': return 'Apresentação';
      case 'ods': return 'Planilha';
      default: return 'Documento';
    }
  };

  const getFileTypeIcon = (fileType: string): string => {
    switch (fileType) {
      case 'odt': return '📝';
      case 'odp': return '📊';
      case 'ods': return '📈';
      default: return '📄';
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando documento...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Erro ao carregar documento</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={onClose}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const fileType = getFileType(document);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-[95vw] h-[95vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b bg-gray-50 rounded-t-lg">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">{getFileTypeIcon(fileType)}</span>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                {document.title}
              </h3>
              <p className="text-sm text-gray-500">
                {getFileTypeLabel(fileType)} • {getFileExtension(document).toUpperCase()}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                if (viewerInstanceRef.current) {
                  viewerInstanceRef.current.full();
                }
              }}
              className="text-gray-500 hover:text-gray-700 p-2 rounded hover:bg-gray-100"
              title="Tela cheia"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl p-2 rounded hover:bg-gray-100"
              title="Fechar"
            >
              ×
            </button>
          </div>
        </div>
        
        {/* Content Area */}
        <div className="flex-1 overflow-hidden relative">
          <div 
            ref={viewerRef}
            className="h-full w-full bg-gray-100"
            style={{ minHeight: '500px' }}
          />
        </div>
        
        {/* Footer com controles */}
        <div className="border-t bg-gray-50 p-3 flex justify-between items-center rounded-b-lg">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">
              {getFileTypeLabel(fileType)} OpenDocument
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  if (viewerInstanceRef.current) {
                    viewerInstanceRef.current.zoom(0.5);
                  }
                }}
                className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-200"
                title="Diminuir zoom"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <span className="text-sm text-gray-600">100%</span>
              <button
                onClick={() => {
                  if (viewerInstanceRef.current) {
                    viewerInstanceRef.current.zoom(2);
                  }
                }}
                className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-200"
                title="Aumentar zoom"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </button>
            </div>
          </div>
          
          <div className="text-sm text-gray-500">
            Use as setas do teclado para navegar
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenDocumentViewer;
